
import React, { useState, useRef, useMemo, useCallback } from 'react';
import { MdsItem, ItemDetail, ItemFile } from '../types';

interface ItemDetailModalProps {
  item: MdsItem;
  onClose: () => void;
  onSave: (updatedDetails: ItemDetail) => void;
}

const ItemDetailModal: React.FC<ItemDetailModalProps> = ({ item, onClose, onSave }) => {
  const imdsInputRef = useRef<HTMLInputElement>(null);
  const rohsInputRef = useRef<HTMLInputElement>(null);
  const millsheetInputRef = useRef<HTMLInputElement>(null);
  const [newSubName, setNewSubName] = useState('');
  
  const [details, setDetails] = useState<ItemDetail>(() => {
    const d = item.details;
    return {
      pdfs: d?.pdfs ? [...d.pdfs] : [],
      customFields: d?.customFields ? [...d.customFields] : [],
      imdsFile: d?.imdsFile ? { ...d.imdsFile } : undefined,
      rohsFile: d?.rohsFile ? { ...d.rohsFile } : undefined,
      millsheetFile: d?.millsheetFile ? { ...d.millsheetFile } : undefined,
      rohsComposition: d?.rohsComposition ? { ...d.rohsComposition } : { 
        pb: '0', cd: '0', hg: '0', cr6: '0',
        pbb: '0', pbde: '0', dehp: '0', pfas: '0', pfoa: '0', pfos: '0'
      },
      rohsLimits: d?.rohsLimits ? { ...d.rohsLimits } : {},
      rohsUnits: d?.rohsUnits ? { ...d.rohsUnits } : {}
    };
  });

  const defaultLimits: Record<string, number> = {
    pb: 1000, cd: 100, hg: 1000, cr6: 1000,
    pbb: 1000, pbde: 1000, dehp: 1000, pfas: 100,
    pfoa: 0.025, pfos: 0.01
  };

  const standardSubstances = useMemo(() => [
    { label: 'LEAD (PB)', key: 'pb' },
    { label: 'CADMIUM (CD)', key: 'cd' },
    { label: 'MERCURY (HG)', key: 'hg' },
    { label: 'HEX-CR (CR6+)', key: 'cr6' },
    { label: 'PBB', key: 'pbb' },
    { label: 'PBDE', key: 'pbde' },
    { label: 'DEHP', key: 'dehp' },
    { label: 'PFAS', key: 'pfas' },
    { label: 'PFOA', key: 'pfoa' },
    { label: 'PFOS', key: 'pfos' },
  ], []);

  const allSubstances = useMemo(() => {
    const composition = details.rohsComposition || {};
    const standards = standardSubstances.map(s => ({ 
      id: `std-${s.key}`,
      label: s.label,
      key: s.key,
      isCustom: false,
      limit: details.rohsLimits?.[s.key] ?? defaultLimits[s.key] ?? 1000,
      unit: details.rohsUnits?.[s.key] ?? 'PPM'
    }));
    
    const standardKeys = standardSubstances.map(s => s.key);
    const customs = Object.keys(composition)
      .filter(k => !standardKeys.includes(k))
      .map(k => ({
        id: `custom-${k}`, 
        label: k.toUpperCase().replace(/_/g, ' '),
        key: k,
        limit: details.rohsLimits?.[k] ?? 1000,
        unit: details.rohsUnits?.[k] ?? 'PPM',
        isCustom: true
      }));

    return [...standards, ...customs];
  }, [details.rohsComposition, details.rohsLimits, details.rohsUnits, standardSubstances]);

  const updateRoHSValue = (key: string, val: string) => {
    setDetails(prev => ({
      ...prev,
      rohsComposition: { ...(prev.rohsComposition || {}), [key]: val }
    }));
  };

  const updateRoHSLimit = (key: string, val: string) => {
    const numValue = parseFloat(val) || 0;
    setDetails(prev => ({
      ...prev,
      rohsLimits: { ...(prev.rohsLimits || {}), [key]: numValue }
    }));
  };

  const updateRoHSUnit = (key: string, val: string) => {
    setDetails(prev => ({
      ...prev,
      rohsUnits: { ...(prev.rohsUnits || {}), [key]: val }
    }));
  };

  const handleRegisterSubstance = () => {
    const trimmed = newSubName.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase().replace(/\s+/g, '_');
    
    // Check for collisions
    if ((details.rohsComposition && details.rohsComposition[key] !== undefined) || standardSubstances.some(s => s.key === key)) {
      alert(`The substance "${trimmed}" already exists in the registry.`);
      return;
    }
    
    setDetails(prev => ({
      ...prev,
      rohsComposition: { ...(prev.rohsComposition || {}), [key]: '0' },
      rohsLimits: { ...(prev.rohsLimits || {}), [key]: 1000 },
      rohsUnits: { ...(prev.rohsUnits || {}), [key]: 'PPM' }
    }));
    setNewSubName('');
  };

  const handleDeleteSubstance = (key: string) => {
    // Safety check: Don't allow deleting standard substances through this method
    if (standardSubstances.some(s => s.key === key)) return;
    
    if (!window.confirm(`Permanently remove custom substance "${key.toUpperCase()}" from this audit record?`)) return;
    
    setDetails(prev => {
      const newComp = { ...prev.rohsComposition };
      const newLimits = { ...prev.rohsLimits };
      const newUnits = { ...prev.rohsUnits };
      
      delete newComp[key];
      delete newLimits[key];
      delete newUnits[key];
      
      return { 
        ...prev, 
        rohsComposition: newComp, 
        rohsLimits: newLimits, 
        rohsUnits: newUnits 
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'IMDS' | 'ROHS' | 'MILLSHEET') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limit file size to 800KB to prevent Firestore document size issues
    if (file.size > 800 * 1024) {
      alert("File is too large. Please upload files smaller than 800KB to ensure cloud sync.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Url = event.target?.result as string;
      const newFile: ItemFile = { 
        id: `${type}-${Date.now()}`, 
        displayName: type, 
        fileName: file.name, 
        url: base64Url, 
        type: type as any
      };
      
      if (type === 'IMDS') setDetails(prev => ({ ...prev, imdsFile: newFile }));
      else if (type === 'ROHS') setDetails(prev => ({ ...prev, rohsFile: newFile }));
      else if (type === 'MILLSHEET') setDetails(prev => ({ ...prev, millsheetFile: newFile }));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-800">
        
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 p-6 flex justify-between items-center sticky top-0 z-10">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              {item.part_number_customer}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">Material Compliance Audit</span>
              <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">{item.part_name_customer}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 space-y-8 bg-slate-50 dark:bg-slate-950/50">
          
          <section className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-1.5 h-12 bg-green-500 rounded-full"></div>
                <div>
                  <h4 className="text-lg font-bold text-slate-900 dark:text-white">Chemical Matrix</h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Update substance concentrations and safety limits</p>
                </div>
              </div>
              
              <div className="flex gap-3">
                 <input 
                   type="text"
                   placeholder="Register new substance..."
                   className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:text-white w-64 transition-all"
                   value={newSubName}
                   onChange={e => setNewSubName(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && handleRegisterSubstance()}
                 />
                 <button 
                   onClick={handleRegisterSubstance}
                   className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-medium hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors shadow-sm"
                 >
                   Add Substance
                 </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {allSubstances.map((sub) => {
                const currentValStr = details.rohsComposition?.[sub.key] || '0';
                const currentVal = parseFloat(currentValStr);
                const isExceed = currentVal > sub.limit;

                return (
                  <div key={sub.id} className={`p-5 rounded-xl border transition-all relative group ${isExceed ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'}`}>
                    
                    <div className="mb-4 flex justify-between items-start">
                      <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{sub.label}</label>
                      {sub.isCustom && (
                        <div className="flex items-center gap-2">
                          <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 text-[10px] px-1.5 py-0.5 font-bold rounded">CUSTOM</span>
                          <button 
                            onClick={() => handleDeleteSubstance(sub.key)}
                            className="text-slate-400 hover:text-red-600 transition-colors"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <span className="text-[10px] font-medium uppercase text-slate-400 block mb-1">Concentration</span>
                        <div className="flex items-baseline gap-2 border-b border-slate-200 dark:border-slate-700 focus-within:border-blue-500 transition-colors pb-1">
                          <input 
                            type="text" 
                            className="w-full bg-transparent border-none font-bold text-2xl outline-none text-slate-900 dark:text-white p-0"
                            value={currentValStr}
                            onChange={e => updateRoHSValue(sub.key, e.target.value)}
                          />
                          <span className="text-xs font-medium text-slate-500 mb-1">{sub.unit}</span>
                        </div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <div className="flex-1">
                          <span className="text-[10px] font-medium uppercase text-slate-400 block mb-1">Limit</span>
                          <input 
                            type="number"
                            className="w-full bg-slate-50 dark:bg-slate-700/50 rounded px-2 py-1 text-xs font-medium outline-none border border-transparent focus:border-blue-500 dark:text-slate-300"
                            value={sub.limit}
                            onChange={e => updateRoHSLimit(sub.key, e.target.value)}
                          />
                        </div>
                        <div className="w-20">
                          <span className="text-[10px] font-medium uppercase text-slate-400 block mb-1">Unit</span>
                          <select 
                            className="w-full bg-slate-50 dark:bg-slate-700/50 rounded px-2 py-1 text-xs font-medium outline-none border border-transparent focus:border-blue-500 dark:text-slate-300"
                            value={sub.unit}
                            onChange={e => updateRoHSUnit(sub.key, e.target.value)}
                          >
                            <option value="PPM">PPM</option>
                            <option value="%">%</option>
                            <option value="MG/KG">MG/KG</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    {isExceed && (
                      <div className="mt-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-bold uppercase py-1.5 px-2 rounded text-center">
                        Threshold Exceeded
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             <section className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col h-full">
                <h4 className="text-sm font-bold uppercase text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-4 bg-green-500 rounded-full"></div>
                  IMDS Vault
                </h4>
                <div className="flex-1 p-6 bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg flex flex-col items-center justify-center text-center">
                  <span className="text-xs font-medium text-slate-400 uppercase mb-2">IMDS Declaration</span>
                  {details.imdsFile ? (
                    <div className="w-full px-4 mb-4">
                      <a 
                        href={details.imdsFile.url} 
                        download={details.imdsFile.fileName}
                        className="text-sm font-medium text-blue-600 hover:text-blue-500 hover:underline truncate block"
                      >
                        {details.imdsFile.fileName}
                      </a>
                      <button 
                        onClick={() => setDetails(prev => ({ ...prev, imdsFile: undefined }))}
                        className="text-[10px] text-red-500 hover:text-red-600 mt-1 font-medium"
                      >
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate w-full mb-4 px-4">No file attached</p>
                  )}
                  <button 
                    onClick={() => imdsInputRef.current?.click()}
                    className="px-4 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors shadow-sm"
                  >
                    {details.imdsFile ? 'Replace File' : 'Upload File'}
                  </button>
                  <input type="file" className="hidden" ref={imdsInputRef} onChange={e => handleFileChange(e, 'IMDS')} />
                </div>
             </section>

             <section className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col h-full">
                <h4 className="text-sm font-bold uppercase text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-4 bg-blue-500 rounded-full"></div>
                  RoHS Report
                </h4>
                <div className="flex-1 p-6 bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg flex flex-col items-center justify-center text-center">
                  <span className="text-xs font-medium text-slate-400 uppercase mb-2">Laboratory Test</span>
                  {details.rohsFile ? (
                    <div className="w-full px-4 mb-4">
                      <a 
                        href={details.rohsFile.url} 
                        download={details.rohsFile.fileName}
                        className="text-sm font-medium text-blue-600 hover:text-blue-500 hover:underline truncate block"
                      >
                        {details.rohsFile.fileName}
                      </a>
                      <button 
                        onClick={() => setDetails(prev => ({ ...prev, rohsFile: undefined }))}
                        className="text-[10px] text-red-500 hover:text-red-600 mt-1 font-medium"
                      >
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate w-full mb-4 px-4">No report attached</p>
                  )}
                  <button 
                    onClick={() => rohsInputRef.current?.click()}
                    className="px-4 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors shadow-sm"
                  >
                    {details.rohsFile ? 'Replace File' : 'Upload File'}
                  </button>
                  <input type="file" className="hidden" ref={rohsInputRef} onChange={e => handleFileChange(e, 'ROHS')} />
                </div>
             </section>

             <section className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col h-full">
                <h4 className="text-sm font-bold uppercase text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <div className="w-1.5 h-4 bg-orange-500 rounded-full"></div>
                  Millsheet Data
                </h4>
                <div className="flex-1 p-6 bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg flex flex-col items-center justify-center text-center">
                  <span className="text-xs font-medium text-slate-400 uppercase mb-2">Material Certificate</span>
                  {details.millsheetFile ? (
                    <div className="w-full px-4 mb-4">
                      <a 
                        href={details.millsheetFile.url} 
                        download={details.millsheetFile.fileName}
                        className="text-sm font-medium text-blue-600 hover:text-blue-500 hover:underline truncate block"
                      >
                        {details.millsheetFile.fileName}
                      </a>
                      <button 
                        onClick={() => setDetails(prev => ({ ...prev, millsheetFile: undefined }))}
                        className="text-[10px] text-red-500 hover:text-red-600 mt-1 font-medium"
                      >
                        Remove File
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate w-full mb-4 px-4">No millsheet attached</p>
                  )}
                  <button 
                    onClick={() => millsheetInputRef.current?.click()}
                    className="px-4 py-2 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors shadow-sm"
                  >
                    {details.millsheetFile ? 'Replace File' : 'Upload File'}
                  </button>
                  <input type="file" className="hidden" ref={millsheetInputRef} onChange={e => handleFileChange(e, 'MILLSHEET')} />
                </div>
             </section>
          </div>

          <section className="bg-white dark:bg-slate-900 p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <h4 className="text-sm font-bold uppercase text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <div className="w-1.5 h-4 bg-yellow-400 rounded-full"></div>
                Notes & Audit Info
              </h4>
              <textarea 
                className="w-full h-32 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:text-white resize-none"
                placeholder="Add audit notes or observations..."
                value={details.customFields?.[0]?.value || ''}
                onChange={(e) => setDetails(prev => ({
                  ...prev,
                  customFields: [{ id: 'note', label: 'Note', value: e.target.value }]
                }))}
              />
           </section>
        </div>

        <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 p-6 flex justify-end gap-4 sticky bottom-0 z-10">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={() => onSave(details)}
            className="px-8 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailModal;
